import type { LocationQuery, RouteLocationRaw } from 'vue-router'

// Where a `web+todo:` link leads. `tag` has no calendar in the link, so the page picks one (`todosEntryRedirect`).
export type WebTodoTarget =
  | { kind: 'route'; location: RouteLocationRaw }
  | { kind: 'tag'; tag: string; query: LocationQuery }

const PREFIX = /^web\+todo:(?:\/\/)?/i

function decode(segment: string): string | null {
  try {
    return decodeURIComponent(segment)
  } catch {
    return null
  }
}

function parseQuery(search: string): LocationQuery {
  const query: LocationQuery = {}
  for (const [key, value] of new URLSearchParams(search)) {
    const known = query[key]
    if (known === undefined) query[key] = value
    else query[key] = [...(Array.isArray(known) ? known : [known]), value]
  }
  return query
}

// The links mirror the pages of the app (a `?query` is the list view of `todos/viewQuery.ts`, handed on as is):
//   web+todo:calendar/<id>[?status=done&sort=due&q=…&tag=…]   -> /todos/<id>
//   web+todo:calendar/<id>/item/<uid>                          -> /todos/<id>/<uid>
//   web+todo:calendar/<id>/tag/<tag>                           -> /todos/<id>/tag/<tag>
//   web+todo:tag/<tag>                                         -> the tag page of the only list
// Anything else is `null`.
export function parseWebTodo(uri: string): WebTodoTarget | null {
  const match = PREFIX.exec(uri.trim())
  if (!match) return null
  const rest = uri.trim().slice(match[0].length)
  const [pathPart, ...searchParts] = rest.split('?')
  const query = parseQuery(searchParts.join('?'))
  const trimmed = pathPart.replace(/\/+$/, '')
  const segments = trimmed === '' ? [] : trimmed.split('/')
  const decoded = segments.map(decode)
  if (decoded.some((segment) => segment === null || segment === '')) return null
  const [head, ...path] = decoded as string[]

  if (head === 'tag' && path.length === 1) return { kind: 'tag', tag: path[0], query }
  if (head !== 'calendar' || !/^\d+$/.test(path[0] ?? '')) return null
  const calendarId = path[0]
  const [what, value, ...more] = path.slice(1)
  if (what === undefined)
    return { kind: 'route', location: { name: 'todo-list', params: { calendarId }, query } }
  if (value === undefined || more.length > 0) return null
  if (what === 'item')
    return { kind: 'route', location: { name: 'todo-detail', params: { calendarId, uid: value } } }
  if (what === 'tag')
    return {
      kind: 'route',
      location: { name: 'todo-tag', params: { calendarId, tag: value }, query },
    }
  return null
}
